import React, { useState, useEffect } from 'react';
import { applicationsApi, jobsApi, candidatesApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import OwnerLensToggle, { useStickyOwnerLens } from '../components/common/OwnerLensToggle';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  MoreVertical,
  ArrowRight,
  User,
  Clock,
  Building2,
  Loader2,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import JobPicker from '../components/jobs/JobPicker';
import ClientStageHeatmap from '../components/pipeline/ClientStageHeatmap';
import JobStageTable from '../components/pipeline/JobStageTable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { canSeeRevenue } from '../lib/perms';
import MissingCtcPill, { isMissingOfferedCtc } from '../components/common/MissingCtcPill';

const STAGES = [
  { id: 'sourced', label: 'Sourced', color: 'bg-slate-100 border-slate-300' },
  { id: 'screening', label: 'Screening', color: 'bg-blue-50 border-blue-300' },
  { id: 'client_submission', label: 'Client Submission', color: 'bg-purple-50 border-purple-300' },
  { id: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-amber-50 border-amber-300' },
  { id: 'interview_selected', label: 'Interview Selected', color: 'bg-emerald-50 border-emerald-300' },
  { id: 'offer_released', label: 'Offer Released', color: 'bg-teal-50 border-teal-300' },
  { id: 'joined', label: 'Joined', color: 'bg-green-50 border-green-300' },
];

const REJECTED_STAGES = ['dropped', 'dropped_out', 'rejected'];

const stageBadgeClass = {
  sourced: 'badge-sourced',
  screening: 'badge-screening',
  client_submission: 'badge-submission',
  interview_scheduled: 'badge-interview',
  interview_selected: 'badge-selected',
  offer_released: 'badge-offer',
  joined: 'badge-joined',
  rejected: 'badge-rejected',
  dropped: 'badge-dropped',
};

const Pipeline = () => {
  const { user } = useAuth();
  const showRevenue = canSeeRevenue(user);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobFilter, setJobFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('pipeline');
  // Shared analytics-tab date range — drives both heatmaps.
  const [analyticsPeriod, setAnalyticsPeriod] = useState('all_time');
  const _today = new Date();
  const _monthStart = new Date(_today.getFullYear(), _today.getMonth(), 1);
  const [analyticsStart, setAnalyticsStart] = useState(_monthStart.toISOString().slice(0, 10));
  const [analyticsEnd, setAnalyticsEnd] = useState(_today.toISOString().slice(0, 10));
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isStageChangeOpen, setIsStageChangeOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [createData, setCreateData] = useState({
    candidate_id: '',
    job_id: '',
    stage: 'sourced',
    remarks: '',
  });
  const [stageChangeData, setStageChangeData] = useState({
    stage: '',
    remarks: '',
    client_feedback: '',
  });
  
  // Candidate search state
  const [candidateSearch, setCandidateSearch] = useState('');
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const [selectedCandidateName, setSelectedCandidateName] = useState('');

  // Ownership lens — manager-only toggle persisted across surfaces (shared
  // with Command Center via the same localStorage key). For recruiters the
  // toggle is hidden and the backend ignores the param.
  const isManager = ['admin', 'delivery_manager', 'team_lead'].includes(user?.role);
  const [ownerLens, setOwnerLens] = useStickyOwnerLens('owner_lens', 'created');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobFilter, ownerLens]);

  const fetchData = async () => {
    try {
      // Pipeline renders all stages on a single Kanban — there's no
      // pagination UI. Without an explicit page_size we'd hit the backend's
      // DEFAULT_PAGE_SIZE = 20 cap and silently drop apps off the board for
      // any recruiter / DM with >20 active applications. Pass the maximum
      // page size (200) so the board reflects what's really in the system.
      const params = { page_size: 200 };
      if (jobFilter && jobFilter !== 'all') params.job_id = jobFilter;
      // Send the manager lens only when the user is actually a manager — the
      // backend ignores it for recruiters but sending the param leaves a
      // confusing trail in audit logs / network tabs.
      if (isManager) params.owner_lens = ownerLens;

      const [appsRes, jobsRes, candidatesRes] = await Promise.all([
        applicationsApi.getAll(params),
        jobsApi.getAll({ page_size: 200 }),
        candidatesApi.getAll({ page_size: 200 }),
      ]);

      // Handle paginated responses
      const appsData = appsRes.data;
      setApplications(appsData.items || (Array.isArray(appsData) ? appsData : []));
      
      const jobsData = jobsRes.data;
      setJobs(jobsData.items || (Array.isArray(jobsData) ? jobsData : []));
      
      const candidatesData = candidatesRes.data;
      setCandidates(candidatesData.items || (Array.isArray(candidatesData) ? candidatesData : []));
    } catch (error) {
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  // Filter candidates based on search term
  const filteredCandidates = candidates.filter(c => 
    c.name?.toLowerCase().includes(candidateSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(candidateSearch.toLowerCase()) ||
    c.phone?.includes(candidateSearch)
  ).slice(0, 10); // Limit to 10 results

  const handleSelectCandidate = (candidate) => {
    setCreateData({ ...createData, candidate_id: candidate.id });
    setSelectedCandidateName(candidate.name);
    setCandidateSearch('');
    setShowCandidateDropdown(false);
  };

  const handleCandidateSearchChange = (e) => {
    const value = e.target.value;
    setCandidateSearch(value);
    setShowCandidateDropdown(value.length > 0);
    // Clear selection if user starts typing again
    if (selectedCandidateName && value !== selectedCandidateName) {
      setCreateData({ ...createData, candidate_id: '' });
      setSelectedCandidateName('');
    }
  };

  const getApplicationsByStage = (stageId) => {
    return applications.filter((app) => app.stage === stageId);
  };

  const getRejectedApplications = () => {
    return applications.filter((app) => REJECTED_STAGES.includes(app.stage));
  };

  const handleCreateApplication = async () => {
    setFormLoading(true);
    try {
      await applicationsApi.create(createData);
      toast.success('Application created successfully');
      setIsCreateOpen(false);
      setCreateData({ candidate_id: '', job_id: '', stage: 'sourced', remarks: '' });
      setCandidateSearch('');
      setSelectedCandidateName('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create application'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleStageChange = async () => {
    setFormLoading(true);
    try {
      // For JOINED transitions, parse and validate the Offered CTC. Empty
      // input falls back to the candidate's profile CTC at aggregation time.
      const payload = { ...stageChangeData };
      if (payload.stage === 'joined') {
        // Joining date is mandatory client-side too — show a friendly message
        // before hitting the backend (which also enforces it as a 400).
        if (!payload.joining_date) {
          toast.error('Joining date is mandatory when marking as Joined.');
          setFormLoading(false);
          return;
        }
        const raw = String(payload.offered_ctc ?? '').trim();
        if (raw === '') {
          payload.offered_ctc = null;
        } else {
          const n = Number(raw);
          if (Number.isNaN(n) || n < 0) {
            toast.error('Offered CTC must be a positive number.');
            setFormLoading(false);
            return;
          }
          // User types CTC in LPA (e.g. 12.5 = ₹12.5L). We persist in rupees
          // so every downstream aggregation (Candidate CTC totals, Closure
          // Strip, closure-of-week Slack, etc.) works in a single unit.
          payload.offered_ctc = Math.round(n * 100000);
        }
      } else {
        delete payload.offered_ctc;
        delete payload.joining_date;
      }
      // Drop-out: reason is mandatory (whitelisted on backend). "Other"
      // also requires custom text.
      if (payload.stage === 'dropped_out') {
        if (!payload.dropped_reason) {
          toast.error('Please select a reason for dropping the candidate.');
          setFormLoading(false);
          return;
        }
        if (payload.dropped_reason === 'other' && !(payload.dropped_reason_text || '').trim()) {
          toast.error('Please describe the reason.');
          setFormLoading(false);
          return;
        }
      } else {
        delete payload.dropped_reason;
        delete payload.dropped_reason_text;
      }
      await applicationsApi.changeStage(selectedApp.id, payload);
      toast.success('Stage updated successfully');
      setIsStageChangeOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update stage'));
    } finally {
      setFormLoading(false);
    }
  };

  const openStageChange = (app, targetStage) => {
    setSelectedApp(app);
    // Pre-fill offered CTC in LPA (÷ 100000) so the number in the input
    // always reflects the label "in Lakhs (LPA)" — backwards compatible
    // with legacy rupee values since the UI converts on submit too.
    const rawCtc = app?.expected_ctc || app?.candidate_expected_ctc
      || app?.current_ctc || app?.candidate_current_ctc || '';
    const prefillLakhs = rawCtc
      ? String(Math.round((Number(rawCtc) / 100000) * 100) / 100)
      : '';
    setStageChangeData({
      stage: targetStage || '',
      remarks: '',
      client_feedback: '',
      offered_ctc: targetStage === 'joined' ? prefillLakhs : '',
      joining_date: targetStage === 'joined'
        ? new Date().toISOString().slice(0, 10)
        : '',
    });
    setIsStageChangeOpen(true);
  };

  const openView = (app) => {
    setSelectedApp(app);
    setIsViewOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pipeline
            </h1>
            <p className="text-slate-600 mt-1">Track candidates through the hiring process</p>
          </div>
          <Button 
            className="bg-teal-700 hover:bg-teal-800" 
            onClick={() => setIsCreateOpen(true)}
            data-testid="add-application-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add to Pipeline
          </Button>
        </div>
      </div>

      {/* Tabbed layout — Pipeline (kanban + filters) vs Analytics (heatmap) */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="pipeline-tabs">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="pipeline" data-testid="pipeline-tab-trigger">Pipeline</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="pipeline-analytics-tab-trigger">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="space-y-4">
          <div className="flex justify-end items-center gap-3">
            {/* Owner-lens toggle — visible only to managers (admin / DM /
                TL). The lens persists across reloads and across Pipeline ↔
                Command Center via the shared `owner_lens` storage key. */}
            <OwnerLensToggle
              value={ownerLens}
              onChange={setOwnerLens}
              visible={isManager}
              testid="pipeline-owner-lens"
            />
            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map((job) => (
                  <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Kanban Board */}
          <div className="kanban-container" data-testid="kanban-board">
            {STAGES.map((stage) => {
              const stageApps = getApplicationsByStage(stage.id);
              return (
                <div key={stage.id} className="kanban-column" data-testid={`stage-${stage.id}`}>
              <div className={`rounded-xl border-2 ${stage.color} h-full flex flex-col overflow-hidden`}>
                {/* Per-column scroll area. Header uses position:sticky so the
                    stage name + count stay pinned at the top while cards
                    scroll behind them. backdrop-blur keeps the header
                    readable when card avatars peek up against its bottom
                    edge. Switched away from Radix ScrollArea because sticky
                    positioning inside its compound viewport is brittle. */}
                <div className="flex-1 overflow-y-auto" data-testid={`stage-${stage.id}-scroll`}>
                  <div className="sticky top-0 z-10 px-4 py-3 border-b border-inherit bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 shadow-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900">{stage.label}</h3>
                      <Badge variant="secondary" className="font-mono">
                        {stageApps.length}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-3 p-3">
                    {stageApps.map((app) => {
                      const missingCtc = canSeeRevenue(user) && isMissingOfferedCtc(app);
                      return (
                      <Card
                        key={app.id}
                        className={`bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer group ${
                          missingCtc ? 'ring-1 ring-amber-300 bg-amber-50/30' : ''
                        }`}
                        onClick={() => openView(app)}
                        data-testid={`app-card-${app.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-teal-700" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{app.candidate_name}</p>
                                <p className="text-xs text-slate-500">{app.job_title}</p>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100">
                                  <MoreVertical className="w-3 h-3" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openStageChange(app); }}>
                                  <ArrowRight className="w-4 h-4 mr-2" /> Move Stage
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          {app.client_name && (
                            <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                              <Building2 className="w-3 h-3" /> {app.client_name}
                            </div>
                          )}
                          {missingCtc && (
                            <div className="mt-2">
                              <MissingCtcPill
                                size="sm"
                                onClick={() => openStageChange(app)}
                                data-testid={`pipeline-missing-ctc-${app.id}`}
                              />
                            </div>
                          )}
                          {app.remarks && (
                            <p className="mt-2 text-xs text-slate-500 line-clamp-2">{app.remarks}</p>
                          )}
                        </CardContent>
                      </Card>
                      );
                    })}
                    {stageApps.length === 0 && (
                      <p className="text-sm text-slate-400 text-center py-8">No candidates</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Rejected/Dropped Column */}
        <div className="kanban-column">
          <div className="rounded-xl border-2 bg-red-50 border-red-200 h-full flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto" data-testid="stage-rejected-scroll">
              <div className="sticky top-0 z-10 px-4 py-3 border-b border-red-200 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-red-700">Rejected/Dropped</h3>
                  <Badge variant="secondary" className="font-mono bg-red-100 text-red-700">
                    {getRejectedApplications().length}
                  </Badge>
                </div>
              </div>
              <div className="space-y-3 p-3">
                {getRejectedApplications().map((app) => (
                  <Card
                    key={app.id}
                    className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer opacity-75"
                    onClick={() => openView(app)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{app.candidate_name}</p>
                          <p className="text-xs text-slate-500">{app.job_title}</p>
                        </div>
                      </div>
                      <Badge className={`mt-2 ${stageBadgeClass[app.stage]}`}>
                        {app.stage}
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          {/* Shared date-range filter — drives BOTH the client heatmap and the
              job-stage table below. Centralised so DMs don't have to set the
              same range twice. */}
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="analytics-shared-filter">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Date range</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="analytics-period-tabs">
              {[
                { key: 'all_time', label: 'All time' },
                { key: 'current_month', label: 'This month' },
                { key: 'last_month', label: 'Last month' },
                { key: 'custom', label: 'Custom' },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setAnalyticsPeriod(t.key)}
                  className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                    analyticsPeriod === t.key
                      ? 'bg-slate-900 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid={`analytics-period-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {analyticsPeriod === 'custom' && (
              <div className="flex items-center gap-2" data-testid="analytics-custom-range">
                <span className="text-xs text-slate-500">From</span>
                <input
                  type="date"
                  value={analyticsStart}
                  max={analyticsEnd}
                  onChange={(e) => setAnalyticsStart(e.target.value)}
                  className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
                  data-testid="analytics-start-date"
                />
                <span className="text-xs text-slate-500">to</span>
                <input
                  type="date"
                  value={analyticsEnd}
                  min={analyticsStart}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setAnalyticsEnd(e.target.value)}
                  className="h-7 px-2 text-xs border border-slate-200 rounded-md bg-white focus:ring-1 focus:ring-teal-500 outline-none"
                  data-testid="analytics-end-date"
                />
                {analyticsStart > analyticsEnd ? (
                  <span className="text-xs text-red-600 font-medium">Start must be ≤ End</span>
                ) : null}
              </div>
            )}
            <span className="text-[10px] text-slate-400 italic ml-auto">
              Applies to both Client × Stage and Job × Stage below
            </span>
          </div>

          {/* Client × Pipeline-Stage heat-map — gives DMs a "where are candidates
              stuck per client" view at a glance. Click a cell to scroll to that
              stage column on the Pipeline tab. */}
          <ClientStageHeatmap
            period={analyticsPeriod}
            startDate={analyticsStart}
            endDate={analyticsEnd}
            onCellClick={({ clientName, stage, stageLabel, value }) => {
              // Switch to Pipeline tab so the kanban scroll target exists.
              setActiveTab('pipeline');
              setTimeout(() => {
                const el = document.querySelector(`[data-testid="stage-${stage}"]`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                  el.classList.add('ring-2', 'ring-teal-400', 'ring-offset-2');
                  setTimeout(() => {
                    el.classList.remove('ring-2', 'ring-teal-400', 'ring-offset-2');
                  }, 1600);
                }
              }, 200);
              toast.success(`${clientName} · ${stageLabel}: ${value} candidate${value === 1 ? '' : 's'}`);
            }}
          />

          {/* Per-job × stage table — drilldown view, complements the client
              heatmap above. Click a cell to filter the kanban to that job. */}
          <JobStageTable
            period={analyticsPeriod}
            startDate={analyticsStart}
            endDate={analyticsEnd}
            onCellClick={({ jobId, jobTitle, stage, stageLabel, value }) => {
              // Filter the kanban to this specific job, switch tabs, then scroll.
              setJobFilter(jobId);
              setActiveTab('pipeline');
              setTimeout(() => {
                const el = document.querySelector(`[data-testid="stage-${stage}"]`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                  el.classList.add('ring-2', 'ring-teal-400', 'ring-offset-2');
                  setTimeout(() => {
                    el.classList.remove('ring-2', 'ring-teal-400', 'ring-offset-2');
                  }, 1600);
                }
              }, 200);
              toast.success(`Filtered to "${jobTitle}" · ${stageLabel}: ${value} candidate${value === 1 ? '' : 's'}`);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Create Application Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => {
        setIsCreateOpen(open);
        if (!open) {
          setCandidateSearch('');
          setSelectedCandidateName('');
          setShowCandidateDropdown(false);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Pipeline</DialogTitle>
            <DialogDescription>Apply a candidate to a job</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Candidate *</Label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={selectedCandidateName || candidateSearch}
                    onChange={handleCandidateSearchChange}
                    onFocus={() => candidateSearch.length > 0 && setShowCandidateDropdown(true)}
                    placeholder="Search candidate by name, email, or phone..."
                    className="pl-9"
                    data-testid="search-candidate"
                  />
                  {selectedCandidateName && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCandidateName('');
                        setCreateData({ ...createData, candidate_id: '' });
                        setCandidateSearch('');
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      ×
                    </button>
                  )}
                </div>
                {showCandidateDropdown && filteredCandidates.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredCandidates.map((candidate) => (
                      <div
                        key={candidate.id}
                        onClick={() => handleSelectCandidate(candidate)}
                        className="px-3 py-2 hover:bg-slate-100 cursor-pointer border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900">{candidate.name}</div>
                        <div className="text-sm text-slate-500 flex gap-2">
                          {candidate.email && <span>{candidate.email}</span>}
                          {candidate.phone && <span>• {candidate.phone}</span>}
                        </div>
                        {candidate.current_company && (
                          <div className="text-xs text-slate-400">{candidate.current_designation} at {candidate.current_company}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {showCandidateDropdown && candidateSearch && filteredCandidates.length === 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-3 text-center text-slate-500">
                    No candidates found
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Job *</Label>
              <JobPicker
                jobs={jobs}
                value={createData.job_id}
                onChange={(v) => setCreateData({ ...createData, job_id: v })}
                placeholder="Select job"
                testid="pipeline-create-job"
              />
            </div>
            <div className="space-y-2">
              <Label>Initial Remarks</Label>
              <Textarea
                value={createData.remarks}
                onChange={(e) => setCreateData({ ...createData, remarks: e.target.value })}
                placeholder="Any initial notes..."
                data-testid="application-remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleCreateApplication}
              disabled={formLoading || !createData.candidate_id || !createData.job_id}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="submit-application-btn"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add to Pipeline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Change Dialog */}
      <Dialog open={isStageChangeOpen} onOpenChange={setIsStageChangeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Stage</DialogTitle>
            <DialogDescription>
              Move {selectedApp?.candidate_name} to a new stage
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Stage *</Label>
              <Select
                value={stageChangeData.stage}
                onValueChange={(v) => setStageChangeData({ ...stageChangeData, stage: v })}
              >
                <SelectTrigger data-testid="select-stage">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="dropped">On Hold</SelectItem>
                  <SelectItem value="dropped_out">Dropped</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                value={stageChangeData.remarks}
                onChange={(e) => setStageChangeData({ ...stageChangeData, remarks: e.target.value })}
                placeholder="Reason for stage change..."
                data-testid="stage-remarks"
              />
            </div>
            <div className="space-y-2">
              <Label>Client Feedback</Label>
              <Textarea
                value={stageChangeData.client_feedback}
                onChange={(e) => setStageChangeData({ ...stageChangeData, client_feedback: e.target.value })}
                placeholder="Feedback from client (if any)..."
                data-testid="client-feedback"
              />
            </div>
            {stageChangeData.stage === 'joined' ? (
              <div className="space-y-3 rounded-md border border-emerald-200 bg-emerald-50/50 p-3" data-testid="pipeline-offered-ctc-block">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1 text-emerald-900">
                    Joining Date
                    <span className="text-rose-600 font-bold">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={stageChangeData.joining_date || ''}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setStageChangeData({ ...stageChangeData, joining_date: e.target.value })}
                    data-testid="pipeline-joining-date-input"
                    className="bg-white"
                    required
                  />
                  <p className="text-[10px] text-emerald-800/80">
                    The actual date the candidate joined. Used as the closure date in all reports.
                  </p>
                </div>
                {showRevenue && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1 text-emerald-900">
                    Offered CTC
                    <span className="text-[10px] text-emerald-700/70 font-normal">· in Lakhs (LPA)</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={stageChangeData.offered_ctc}
                    onChange={(e) => setStageChangeData({ ...stageChangeData, offered_ctc: e.target.value })}
                    placeholder="e.g. 12.5"
                    data-testid="pipeline-offered-ctc-input"
                    className="bg-white"
                  />
                  {stageChangeData.offered_ctc && !Number.isNaN(Number(stageChangeData.offered_ctc)) && Number(stageChangeData.offered_ctc) > 0 ? (
                    <p
                      className="text-[11px] text-emerald-900 font-medium"
                      data-testid="pipeline-offered-ctc-preview"
                    >
                      = ₹{Number(stageChangeData.offered_ctc).toLocaleString('en-IN')} L (₹{(Number(stageChangeData.offered_ctc) * 100000).toLocaleString('en-IN')})
                    </p>
                  ) : null}
                  <p className="text-[10px] text-emerald-800/80">
                    Locks the candidate CTC for this closure. Leave blank to fall back to the candidate's profile CTC.
                  </p>
                </div>
                )}
              </div>
            ) : null}
            {stageChangeData.stage === 'dropped_out' ? (
              <div className="space-y-3 rounded-md border border-rose-200 bg-rose-50/40 p-3" data-testid="pipeline-dropped-block">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1 text-rose-900">
                    Why did the candidate drop?
                    <span className="text-rose-600 font-bold">*</span>
                  </Label>
                  <Select
                    value={stageChangeData.dropped_reason || ''}
                    onValueChange={(v) => setStageChangeData({ ...stageChangeData, dropped_reason: v })}
                  >
                    <SelectTrigger data-testid="pipeline-dropped-reason-select" className="bg-white">
                      <SelectValue placeholder="Select a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="better_offer">Got better offer</SelectItem>
                      <SelectItem value="counter_offer">Counter-offer accepted</SelectItem>
                      <SelectItem value="compensation_mismatch">Compensation mismatch</SelectItem>
                      <SelectItem value="location_mismatch">Location mismatch</SelectItem>
                      <SelectItem value="personal_reasons">Personal reasons</SelectItem>
                      <SelectItem value="lost_contact">Lost contact</SelectItem>
                      <SelectItem value="other">Other (specify)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {stageChangeData.dropped_reason === 'other' ? (
                  <div className="space-y-2">
                    <Label className="text-rose-900">Specify reason</Label>
                    <Input
                      value={stageChangeData.dropped_reason_text || ''}
                      onChange={(e) => setStageChangeData({ ...stageChangeData, dropped_reason_text: e.target.value.slice(0, 200) })}
                      placeholder="e.g. Family emergency"
                      data-testid="pipeline-dropped-reason-text"
                      className="bg-white"
                    />
                  </div>
                ) : null}
                <p className="text-[10px] text-rose-800/80">
                  Aggregated drop reasons feed into the Job Summary AI insight so managers can spot patterns.
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              onClick={handleStageChange}
              disabled={formLoading || !stageChangeData.stage}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="update-stage-btn"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Application Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedApp?.candidate_name}</DialogTitle>
            <DialogDescription>{selectedApp?.job_title}</DialogDescription>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Badge className={stageBadgeClass[selectedApp.stage]}>
                  {selectedApp.stage.replace('_', ' ')}
                </Badge>
                {selectedApp.client_name && (
                  <Badge variant="outline">{selectedApp.client_name}</Badge>
                )}
              </div>

              {selectedApp.remarks && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Latest Remarks</h4>
                  <p className="text-slate-600 text-sm">{selectedApp.remarks}</p>
                </div>
              )}

              <div>
                <h4 className="font-medium text-slate-900 mb-2">Stage History</h4>
                <div className="space-y-3">
                  {selectedApp.stage_history?.map((entry, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-2 h-2 rounded-full bg-teal-500 mt-2"></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={stageBadgeClass[entry.stage] || 'bg-slate-100'}>
                            {entry.stage.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {format(new Date(entry.date), 'MMM dd, yyyy HH:mm')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          by {entry.owner_name}
                        </p>
                        {entry.remarks && (
                          <p className="text-sm text-slate-500 mt-1">{entry.remarks}</p>
                        )}
                        {entry.client_feedback && (
                          <p className="text-sm text-slate-500 mt-1 italic">
                            Client: {entry.client_feedback}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => { setIsViewOpen(false); openStageChange(selectedApp); }}
                  data-testid="move-stage-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-2" /> Move Stage
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pipeline;
